import type { AppError } from '@aibi/shared';
import { Badge, Button, Card, cn, Field, Input, Spinner } from '@aibi/ui';
import { ArrowLeft, CheckCircle2, RefreshCw, Rocket, Table2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  Connection,
  DiscoveredTable,
  Discovery,
  Draft,
  Preview,
  Publication,
  Recommendation,
  RecommendationGroup,
} from '../services/admin-api';
import { useAdminServices } from '../state/admin-session';
import { ColumnMapping } from './ColumnMapping';
import {
  initialAssignments,
  summarize,
  toColumnAssignments,
  type Assignments,
} from './column-mapping';
import { ErrorNotice, Notice } from './ErrorNotice';
import { isSelectable } from './form-rules';

export interface PublishWizardProps {
  readonly connection: Connection;
  readonly onCancel: () => void;
  readonly onPublished: (publication: Publication) => void;
  readonly onOpenAnalysis: (modelId: string) => void;
}

/** Sugerencias que no dependen del uso de cada columna: se aprueban aparte. */
const GROUPS: readonly {
  readonly id: RecommendationGroup;
  readonly label: string;
  readonly help: string;
}[] = [
  { id: 'relationship', label: 'Relaciones', help: 'Como se unen las tablas entre si.' },
  { id: 'key', label: 'Claves', help: 'Columnas que identifican cada fila.' },
];
const STRUCTURAL: ReadonlySet<RecommendationGroup> = new Set(GROUPS.map((group) => group.id));

type Phase =
  | { readonly step: 'loading' }
  | { readonly step: 'discovering' }
  | { readonly step: 'error'; readonly error: AppError }
  | { readonly step: 'ready'; readonly discovery: Discovery };

/**
 * Del descubrimiento al modelo publicado: tablas, perfil y vista previa, sugerencias que
 * el usuario aprueba, validacion ejecutable y publicacion explicita. Nada se publica sin
 * pulsar "Publicar".
 */
export function PublishWizard({
  connection,
  onCancel,
  onPublished,
  onOpenAnalysis,
}: PublishWizardProps) {
  const { admin } = useAdminServices();
  const [phase, setPhase] = useState<Phase>({ step: 'loading' });

  const discover = async () => {
    setPhase({ step: 'discovering' });
    const result = await admin.discover(connection.id);
    setPhase(
      result.ok
        ? { step: 'ready', discovery: result.value }
        : { step: 'error', error: result.error },
    );
  };

  useEffect(() => {
    const effect = { cancelled: false };
    const cancelled = () => effect.cancelled;
    void admin.lastDiscovery(connection.id).then(async (result) => {
      if (cancelled()) return;
      if (!result.ok) {
        setPhase({ step: 'error', error: result.error });
        return;
      }
      if (result.value !== null) {
        setPhase({ step: 'ready', discovery: result.value });
        return;
      }
      setPhase({ step: 'discovering' });
      const discovered = await admin.discover(connection.id);
      if (cancelled()) return;
      setPhase(
        discovered.ok
          ? { step: 'ready', discovery: discovered.value }
          : { step: 'error', error: discovered.error },
      );
    });
    return () => {
      effect.cancelled = true;
    };
  }, [admin, connection.id]);

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          <ArrowLeft /> Conexiones
        </Button>
        <h2 className="text-lg font-semibold">
          {connection.queryable ? 'Crear un modelo de analisis' : 'Explorar'} · {connection.name}
        </h2>
        {!connection.queryable && <Badge variant="warning">Solo exploracion</Badge>}
      </div>
      {phase.step === 'loading' && <Spinner label="Cargando" />}
      {phase.step === 'discovering' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Spinner label="Explorando" /> Explorando la fuente: tablas, columnas y perfil de los
          datos...
        </div>
      )}
      {phase.step === 'error' && (
        <>
          <ErrorNotice error={phase.error} />
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            onClick={() => void discover()}
          >
            <RefreshCw /> Volver a explorar
          </Button>
        </>
      )}
      {phase.step === 'ready' && (
        <WizardBody
          key={phase.discovery.discoveredAt}
          connection={connection}
          discovery={phase.discovery}
          onRediscover={() => void discover()}
          onPublished={onPublished}
          onOpenAnalysis={onOpenAnalysis}
        />
      )}
    </div>
  );
}

function WizardBody({
  connection,
  discovery,
  onRediscover,
  onPublished,
  onOpenAnalysis,
}: {
  readonly connection: Connection;
  readonly discovery: Discovery;
  readonly onRediscover: () => void;
  readonly onPublished: (publication: Publication) => void;
  readonly onOpenAnalysis: (modelId: string) => void;
}) {
  const { admin } = useAdminServices();
  const selectable = discovery.tables.filter(isSelectable);
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(selectable.length === 1 ? selectable.map((table) => table.id) : []),
  );
  const relevant = useMemo(
    () =>
      discovery.recommendations.filter(
        (rec) =>
          STRUCTURAL.has(rec.group) &&
          (rec.objectIds.length === 0 || rec.objectIds.some((id) => selected.has(id))),
      ),
    [discovery.recommendations, selected],
  );
  const [accepted, setAccepted] = useState<ReadonlySet<string>>(
    () => new Set(discovery.recommendations.filter((rec) => rec.suggested).map((rec) => rec.id)),
  );
  const [assignments, setAssignments] = useState<Assignments>(() => initialAssignments(selectable));
  const chosenTables = discovery.tables.filter((table) => selected.has(table.id));
  const summary = summarize(chosenTables, assignments);
  const nothingAssigned = summary.dimensions.length === 0 && summary.measures.length === 0;
  const [name, setName] = useState(connection.name);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<'validate' | 'publish' | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const [published, setPublished] = useState<Publication | null>(null);

  const invalidate = () => {
    setDraft(null);
    setError(null);
  };
  const toggle = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const validate = async () => {
    if (selected.size === 0 || name.trim() === '' || nothingAssigned) return;
    setBusy('validate');
    setError(null);
    const relevantIds = new Set(relevant.map((rec) => rec.id));
    const result = await admin.createDraft({
      connectionId: connection.id,
      objectIds: [...selected],
      name: name.trim(),
      acceptedRecommendationIds: [...accepted].filter((id) => relevantIds.has(id)),
      columns: toColumnAssignments(chosenTables, assignments),
    });
    setBusy(null);
    if (result.ok) setDraft(result.value);
    else setError(result.error);
  };

  const publish = async () => {
    if (draft === null) return;
    setBusy('publish');
    setError(null);
    const result = await admin.publish(draft.draftId);
    setBusy(null);
    if (result.ok) {
      setPublished(result.value);
      onPublished(result.value);
    } else {
      setError(result.error);
    }
  };

  if (published) {
    return (
      <Card className="items-center gap-3 p-8 text-center">
        <CheckCircle2 aria-hidden className="size-8 text-success" />
        <h3 className="text-base font-semibold">Modelo publicado: {published.name}</h3>
        <p className="text-sm text-muted-foreground">
          Ya esta disponible para todas las personas que usan el analisis.
        </p>
        <Button
          onClick={() => {
            onOpenAnalysis(published.modelId);
          }}
        >
          Abrir en analisis
        </Button>
      </Card>
    );
  }

  return (
    <>
      {discovery.truncated && (
        <Notice tone="warning">
          La fuente tiene mas tablas de las que se exploran de una vez; solo se muestran las
          primeras.
        </Notice>
      )}
      {discovery.issues.map((issue) => (
        <Notice key={issue} tone="warning">
          {issue}
        </Notice>
      ))}

      <Section
        number={1}
        title="Elija las tablas"
        action={
          <Button size="sm" variant="ghost" onClick={onRediscover}>
            <RefreshCw /> Volver a explorar
          </Button>
        }
      >
        <TableChooser
          tables={discovery.tables}
          selected={selected}
          onToggle={(id) => {
            setSelected((current) => toggle(current, id));
            invalidate();
          }}
        />
      </Section>

      {selected.size > 0 && (
        <Section number={2} title="Revise el perfil y la vista previa">
          <TableProfiles
            connectionId={connection.id}
            tables={discovery.tables.filter((table) => selected.has(table.id))}
          />
        </Section>
      )}

      {connection.queryable && selected.size > 0 && (
        <>
          <Section number={3} title="Asigne el uso de cada columna">
            <p className="text-xs text-muted-foreground">
              Se parte de una sugerencia automatica; cambie lo que necesite. Solo se crea lo que
              quede asignado aqui.
            </p>
            <ColumnMapping
              tables={chosenTables}
              assignments={assignments}
              onChange={(key, assignment) => {
                setAssignments((current) => new Map(current).set(key, assignment));
                invalidate();
              }}
              onReset={() => {
                setAssignments(initialAssignments(selectable));
                invalidate();
              }}
            />
            {relevant.length > 0 && (
              <>
                <p className="text-xs text-muted-foreground">
                  Relaciones y claves sugeridas. Solo se aplicara lo que deje marcado.
                </p>
                <RecommendationList
                  recommendations={relevant}
                  accepted={accepted}
                  onToggle={(id) => {
                    setAccepted((current) => toggle(current, id));
                    invalidate();
                  }}
                />
              </>
            )}
          </Section>

          <Section number={4} title="Nombre y validacion">
            <Field
              label="Nombre del modelo"
              error={name.trim() === '' ? 'Escriba un nombre.' : null}
            >
              {(control) => (
                <Input
                  {...control}
                  value={name}
                  maxLength={120}
                  onChange={(event) => {
                    setName(event.target.value);
                    invalidate();
                  }}
                />
              )}
            </Field>
            {nothingAssigned && (
              <Notice tone="warning">
                Asigne al menos una columna como dimension o medida para validar el modelo.
              </Notice>
            )}
            <Button
              size="sm"
              variant="outline"
              className="self-start"
              disabled={busy !== null || name.trim() === '' || nothingAssigned}
              onClick={() => void validate()}
            >
              {busy === 'validate' ? 'Validando...' : 'Validar modelo'}
            </Button>
            {draft && <DraftSummary draft={draft} />}
          </Section>

          <Section number={5} title="Publicar">
            <p className="text-xs text-muted-foreground">
              Al publicar, el modelo queda disponible en Analisis. Puede retirarlo despues.
            </p>
            <Button
              className="self-start"
              disabled={draft === null || busy !== null}
              onClick={() => void publish()}
            >
              <Rocket /> {busy === 'publish' ? 'Publicando...' : 'Publicar modelo'}
            </Button>
          </Section>
        </>
      )}
      {!connection.queryable && (
        <Notice tone="warning">
          Esta fuente es solo para exploracion. Para analizarla, guarde la hoja como CSV y cree una
          conexion CSV.
        </Notice>
      )}
      {error && <ErrorNotice error={error} />}
    </>
  );
}

function Section({
  number,
  title,
  action,
  children,
}: {
  readonly number: number;
  readonly title: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <Card className="gap-3 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <span className="flex size-5 items-center justify-center rounded-full bg-primary/10 text-[11px] text-primary">
            {number}
          </span>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </Card>
  );
}

function TableChooser({
  tables,
  selected,
  onToggle,
}: {
  readonly tables: readonly DiscoveredTable[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
}) {
  if (tables.length === 0)
    return <Notice tone="info">No se encontraron tablas en esta fuente.</Notice>;
  return (
    <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
      {tables.map((table) => {
        const enabled = isSelectable(table);
        return (
          <li key={table.id} className="px-3 py-2">
            <label
              className={cn(
                'flex items-start gap-3 text-sm',
                enabled ? 'cursor-pointer' : 'opacity-60',
              )}
            >
              <input
                type="checkbox"
                className="mt-1 accent-[var(--color-primary)]"
                checked={selected.has(table.id)}
                disabled={!enabled}
                onChange={() => {
                  onToggle(table.id);
                }}
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-2 font-medium">
                  <Table2 aria-hidden className="size-3.5 text-muted-foreground" />
                  {table.namespace ? `${table.namespace}.${table.name}` : table.name}
                  {table.status === 'partial' && (
                    <Badge variant="warning">Exploracion parcial</Badge>
                  )}
                  {!enabled && <Badge variant="destructive">No se pudo leer</Badge>}
                </span>
                <span className="text-xs text-muted-foreground">
                  {table.columns.length} columnas
                  {table.rows !== null &&
                    ` · ${table.rowsApproximate ? 'aprox. ' : ''}${table.rows.toLocaleString('es')} filas`}
                </span>
                {table.issues.slice(0, 3).map((issue) => (
                  <span key={issue} className="text-xs text-warning">
                    {issue}
                  </span>
                ))}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value.toLocaleString('es');
  if (typeof value === 'string' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

function TableProfiles({
  connectionId,
  tables,
}: {
  readonly connectionId: string;
  readonly tables: readonly DiscoveredTable[];
}) {
  const [activeId, setActiveId] = useState(tables[0]?.id ?? '');
  const active = tables.find((table) => table.id === activeId) ?? tables[0];
  if (!active) return null;
  return (
    <div className="flex flex-col gap-3">
      {tables.length > 1 && (
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Tablas seleccionadas">
          {tables.map((table) => (
            <button
              key={table.id}
              type="button"
              role="tab"
              aria-selected={table.id === active.id}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs',
                table.id === active.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent',
              )}
              onClick={() => {
                setActiveId(table.id);
              }}
            >
              {table.name}
            </button>
          ))}
        </div>
      )}
      <div className="overflow-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 font-medium">Columna</th>
              <th className="px-2 py-1.5 font-medium">Tipo</th>
              <th className="px-2 py-1.5 font-medium">Vacios</th>
              <th className="px-2 py-1.5 font-medium">Distintos</th>
              <th className="px-2 py-1.5 font-medium">Minimo</th>
              <th className="px-2 py-1.5 font-medium">Maximo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {active.columns.map((column) => (
              <tr key={column.name}>
                <td className="px-2 py-1.5 font-medium">{column.name}</td>
                <td className="px-2 py-1.5">{column.logicalType}</td>
                <td className="px-2 py-1.5">{formatValue(column.nullCount)}</td>
                <td className="px-2 py-1.5">{formatValue(column.distinctCount)}</td>
                {column.redacted ? (
                  <td colSpan={2} className="px-2 py-1.5 italic text-muted-foreground">
                    Oculto (dato sensible)
                  </td>
                ) : (
                  <>
                    <td className="px-2 py-1.5">{formatValue(column.min)}</td>
                    <td className="px-2 py-1.5">{formatValue(column.max)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PreviewTable key={active.id} connectionId={connectionId} objectId={active.id} />
    </div>
  );
}

function PreviewTable({
  connectionId,
  objectId,
}: {
  readonly connectionId: string;
  readonly objectId: string;
}) {
  const { admin } = useAdminServices();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const result = await admin.preview(connectionId, objectId);
    setLoading(false);
    if (result.ok) setPreview(result.value);
    else setError(result.error);
  };

  if (preview === null) {
    return (
      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? 'Cargando...' : 'Ver las primeras filas'}
        </Button>
        {error && <ErrorNotice error={error} />}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="max-h-72 overflow-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted text-left text-muted-foreground">
            <tr>
              {preview.columns.map((column) => (
                <th key={column.name} className="whitespace-nowrap px-2 py-1.5 font-medium">
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {preview.rows.map((row, index) => (
              <tr key={index}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="whitespace-nowrap px-2 py-1">
                    {formatValue(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Vista previa de {preview.rows.length} filas. Las columnas sensibles no se muestran.
      </p>
    </div>
  );
}

function RecommendationList({
  recommendations,
  accepted,
  onToggle,
}: {
  readonly recommendations: readonly Recommendation[];
  readonly accepted: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {GROUPS.map((group) => {
        const items = recommendations.filter((rec) => rec.group === group.id);
        if (items.length === 0) return null;
        return (
          <fieldset key={group.id} className="flex flex-col gap-1">
            <legend className="text-xs font-semibold">
              {group.label}{' '}
              <span className="font-normal text-muted-foreground">· {group.help}</span>
            </legend>
            <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
              {items.map((rec) => (
                <li key={rec.id} className="px-3 py-2">
                  <label className="flex cursor-pointer items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1 accent-[var(--color-primary)]"
                      checked={accepted.has(rec.id)}
                      onChange={() => {
                        onToggle(rec.id);
                      }}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{rec.label}</span>
                        <span className="text-xs text-muted-foreground">{rec.detail}</span>
                        <Badge
                          variant={
                            rec.confidence >= 0.8
                              ? 'success'
                              : rec.confidence >= 0.6
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          {Math.round(rec.confidence * 100)}%
                        </Badge>
                      </span>
                      <span className="text-xs text-muted-foreground">{rec.reason}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}

function DraftSummary({ draft }: { readonly draft: Draft }) {
  const warnings = draft.diagnostics.filter((item) => item.severity !== 'error');
  return (
    <div className="flex flex-col gap-2">
      <Notice tone="info">
        <p className="font-medium text-success">
          El modelo es valido y responde a consultas reales.
        </p>
        <p>
          {draft.dimensions.length} dimensiones, {draft.measures.length} medidas
          {draft.relationships > 0 ? `, ${String(draft.relationships)} relaciones` : ''}.
        </p>
      </Notice>
      <div className="grid gap-2 sm:grid-cols-2">
        <NameList title="Dimensiones" items={draft.dimensions} />
        <NameList title="Medidas" items={draft.measures} />
      </div>
      {warnings.map((item) => (
        <Notice key={item.message} tone="warning">
          {item.message}
        </Notice>
      ))}
    </div>
  );
}

function NameList({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly { id: string; name: string }[];
}) {
  return (
    <div className="rounded-lg border border-border p-2">
      <p className="mb-1 text-xs font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground">
        {items.length === 0
          ? 'Ninguna'
          : items
              .slice(0, 30)
              .map((item) => item.name)
              .join(', ')}
        {items.length > 30 ? ` y ${String(items.length - 30)} mas` : ''}
      </p>
    </div>
  );
}
